import { Formik } from 'formik';
import React, { useContext } from 'react';
import * as Yup from 'yup';

// components
import SubmitButton from '@atoms/SubmitButton';
import TextInput from '@atoms/TextInput';
import { BodyMedium, H3, LinkMedium } from '@styled';

// functionality
import { MembershipContext } from '../../../../context/MembershipProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { searchMemberByName } from '@general/api/memberAPI';
import { emailRegex } from '@general/email';
import { capitalize } from '@general/utility';
import useCompaniesFromApi from '@hooks/apiLegacy/useCompaniesFromAPI';
import SearchContainer from '@react/components/search/SearchContainer';
import { Configure } from 'react-instantsearch-dom';
import AutocompleteCompanyNames from '@molecules/AutocompleteCompanyNames';

const initialValues = {
    companyId: '-1',
    companyName: '',
    email: '',
};

const validationSchema = Yup.object({
    companyId: Yup.string(),
    companyName: Yup.string().required('Company Name is required'),
    email: Yup.string()
        .matches(emailRegex, 'Email is invalid.')
        .required('Email is required.'),
});

const MembershipNameMatches = () => {
    const { matchResultsByName, setMatchResultsByCompanyAndEmail, setMatchResultsRejected } = useContext(MembershipContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { companies } = useCompaniesFromApi();
    const filters = 'status:Active';

    const searchForAccount = async ({ companyName, email }) => {
        setIsLoading(true);
        const response = await searchMemberByName({
            firstName: encodeURIComponent(matchResultsByName.first_name),
            lastName: encodeURIComponent(matchResultsByName.last_name),
            previousCompany: encodeURIComponent(companyName),
            previousEmail: encodeURIComponent(email),
        });

        const data = response?.data?.data;
        setMatchResultsByCompanyAndEmail(data);
        setIsLoading(false);
    };

    if (!matchResultsByName || !companies) {
        return null;
    }

    const { count, first_name: firstName, last_name: lastName } = matchResultsByName;

    return (
        <div>
            <div>
                <H3>
                    {count} Match{count > 1 ? 'es' : ''} found for {capitalize(firstName)} {capitalize(lastName)}
                </H3>

                <BodyMedium className="mt-1.125rem">
                    Based on your name, We think you may already have an account.
                </BodyMedium>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);

                        await searchForAccount({
                            companyName: values.companyName,
                            email: values.email,
                        });

                        setSubmitting(false);
                    }}
                >
                    {({ values, errors, dirty, isSubmitting, isValid, setFieldValue, submitForm, setFieldTouched }) => {
                        return (
                            <>
                                <div className="mt-1rem">
                                    <SearchContainer key="company-name-search" index="companies">
                                        <Configure clickAnalytics filters={filters} hitsPerPage={10} />
                                        <AutocompleteCompanyNames
                                            name="companyName"
                                            label="What is your previous company?"
                                            onClear={() => setFieldValue('companyID', initialValues.companyId)}
                                            onSelect={selection => {
                                                const companyId = selection.objectID.split('-')[1];
                                                setFieldValue('companyId', companyId);
                                            }}
                                        />
                                    </SearchContainer>
                                </div>

                                <div className="mt-1rem">
                                    <TextInput
                                        label="What is your previous email address?"
                                        name="email"
                                        type="email"
                                        onBlur={() => {
                                            setFieldTouched('email', true);
                                            if (isValid) {
                                                submitForm();
                                            }
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                submitForm();
                                            }
                                        }}
                                    />
                                </div>

                                <div className="mt-1rem d-flex align-items-center">
                                    <SubmitButton
                                        isDisabled={!dirty || (dirty && !isValid)}
                                        isSubmitting={isSubmitting}
                                        submittingText="Finding Account..."
                                        onClick={() => submitForm()}
                                    >
                                        Find My Account
                                    </SubmitButton>

                                    <LinkMedium className="ml-4 mt-4" onClick={() => setMatchResultsRejected(true)}>
                                        This is my first ICSC account.
                                    </LinkMedium>
                                </div>
                            </>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default MembershipNameMatches;
